import React, { useEffect, useMemo } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { CenteredLoadingSpinner } from '@ole-ui/ole-ui-components';

import GenericViewError from 'components/Error/GenericViewError';
import { handleError } from 'services/ErrorService';
import { getLMSClassByOfferingId } from 'services/LMSClassService';
import { getVTClassByUUID } from 'services/VTClassService';
import useMobxStores from 'hooks/useMobxStores';
import DashboardStore from './store';
import './styles.scss';

const ClassDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'class-dashboard' */ 'components/Classes/Dashboard'
    ) as Promise<{
      default: React.ComponentType<{
        data: object;
      }>;
    }>,
);

type DashboardRouteParams = {
  uuid?: string;
  title?: string;
};

type VtClassRecord = Record<string, unknown> & { slug: string };

/**
 * Instructor VT class dashboard for /instructor-dashboard/classes/:uuid.
 * Premium sessions use views/PremiumSession/Dashboard via sessions:view.
 */
const InstructorDashboardClassView = () => {
  const { routerStore, userStore } = useMobxStores();
  const params = ((routerStore.route as { params?: DashboardRouteParams })
    ?.params ?? {}) as DashboardRouteParams;
  const { uuid, title } = params;

  const store = useMemo(() => {
    const dashboardStore = new DashboardStore();
    dashboardStore.title = title ?? '';
    return dashboardStore;
  }, [title, uuid]);

  useEffect(() => {
    document.title = title || store.title;
  }, [store.title, title]);

  useEffect(() => {
    if (!uuid) {
      return;
    }

    getVTClassByUUID(uuid).then((response) => {
      const data = response as VtClassRecord;
      const offeringId = data.slug.replace(/lms_/, '');
      const enriched = { ...data, offeringId };

      store.data = enriched;
      store.userSubscription = userStore.subscription;

      getLMSClassByOfferingId(offeringId).then((classData) => {
        store.title = `${store.title} - ${classData.name}`;
        document.title = store.title;
      });
    });
  }, [store, userStore, uuid]);

  return (
    <div className="instructor-dashboard-class-view">
      <React.Suspense fallback={<CenteredLoadingSpinner />}>
        {store.data && <ClassDashboard data={toJS(store.data)} />}
      </React.Suspense>
    </div>
  );
};

export default withErrorBoundary(
  observer(InstructorDashboardClassView),
  GenericViewError,
  handleError,
);
